import {
  ApiProductState,
  PaymentMethod,
  SimCardAddressInput,
  SimCardType2,
} from "@phonero/common-graphql/types"
import { useEffect, useState } from "react"
import {
  ResponsiveMode,
  useResponsiveMode,
  WizardNavigationProps,
} from "@phonero/phonero-ui-lib"
import {
  PuxContent,
  PuxFooter,
  PuxTypography,
  useTranslation,
} from "@phonero/pux-react"
import {
  AddSimCardDocument,
  AddSimCardMutationVariables,
  InlineErrors,
  OrderStatus,
  pushOrderQueueItem,
  SimCardFragment,
  SimCardOrderFragment,
  SimCardProductFragment,
  StepButtons,
  useAppLazyQuery,
  useAppMutation,
  useFaroClient,
  OrderSimCardDocument,
  OrderSimCardMutationVariables,
  SimZipCodeDocument,
} from "@phonero/common-ux"
import css from "./SimCardWizard.module.scss"
import { Data } from "./AddressStep"
import { formatTelephoneNumber } from "@phonero/pux-core"
import i18n from "i18next"
import classNames from "classnames"
import PriceMvaInfo from "../PriceMvaInfo"
import { uncompletedPurchases } from "../../../../util/reactiveVars"
import { getCurrentAppScheme } from "../../../../platform"
import { routes } from "../../../../routes"
import { useAppRouterDP } from "../../../../util/useAppRouterDP"

export interface ISimCardWizardStep1Props {
  subscriptionId: string
  address: SimCardAddressInput
  simWithoutPin: boolean
  onClose?: () => void
  cards?: (SimCardProductFragment | SimCardFragment | SimCardOrderFragment)[] | undefined
  card?: SimCardProductFragment | SimCardFragment | SimCardOrderFragment | undefined
  sendSimcard: boolean
  userName?: string
  msisdn?: string
  embeddedSim: boolean
  embeddedSimEmail?: string
}
const paymentMethodSortIndex = [PaymentMethod.Vippsrecurring, PaymentMethod.Mobile]
export const ConfirmStep = ({
  subscriptionId,
  address,
  sendSimcard,
  simWithoutPin,
  onClose,
  card,
  cards,
  children,
  data,
  onPrev,
  userName,
  msisdn,
  embeddedSim,
  embeddedSimEmail,
}: ISimCardWizardStep1Props &
  WizardNavigationProps & { data: Data } & { children?: React.ReactNode }) => {
  const responsiveMode = useResponsiveMode()
  const { t } = useTranslation()

  const currentProduct = card ??
    cards?.sort(
      (c1, c2) =>
        (c1.paymentMethods
          ? paymentMethodSortIndex.indexOf(c1.paymentMethods[0])
          : 0) -
        (c2.paymentMethods
          ? paymentMethodSortIndex.indexOf(c2.paymentMethods[0])
          : 0)
    )?.[0] ?? {
      productId: "",
      name: "",
      price: null,
      description: "",
      paymentMethods: [] as PaymentMethod[],
      __typename: "",
      state: ApiProductState.Error
    }

  const isOrderOfNew =
    currentProduct.__typename === "SimCardProduct" &&
    currentProduct.state === ApiProductState.Available
  const itemWithPrice = isOrderOfNew
    ? (currentProduct as SimCardProductFragment)
    : undefined
  const isPaymentMethodVippsRecurring = (currentProduct.paymentMethods?.indexOf(PaymentMethod.Vippsrecurring) ?? -1) > -1
  const isPaymentMethodMobile = (currentProduct.paymentMethods?.indexOf(PaymentMethod.Mobile) ?? -1) > -1

  const cardType = data.cardType
  const [orderId, setOrderId] = useState("")
  const faroClient = useFaroClient()
  const { goTo } = useAppRouterDP()

  const [addSimcard, { error: addSimCardError, ...addSimcardResult }] =
    useAppMutation(AddSimCardDocument, {
      onCompleted: (d) => {
        const { paymentURI, paymentMethod, vippsAgreementId } = d?.addSimCard
          ?.externalPayment ?? {
          paymentURI: undefined,
          paymentMethod: undefined,
          vippsAgreementId: undefined,
        }
        if (!d?.addSimCard?.orderId) {
          return
        }
        if (paymentMethod === PaymentMethod.Vippsrecurring) {
          if (!paymentURI) {
            return
          }
          // Store the purchase-details into local storage
          uncompletedPurchases.add({
            paymentMethod: paymentMethod,
            paymentURI: paymentURI,
            orderID: d?.addSimCard?.orderId,
            productID: currentProduct.productId,
            productName: currentProduct.name,
            paymentID: vippsAgreementId ?? "",
            source: "localStorage",
            isRedirected: false,
            productText: currentProduct.name ?? "",
            amount: currentProduct.price! * 1.25,
            date: new Date(),
          })
          goTo("vipps-recurring-redirect", null, {
            paymentID: vippsAgreementId,
          }).paymentCallbackVippsRecurring()
        } else {
          const orderId = d?.addSimCard?.orderId
          setOrderId(orderId)
          pushOrderQueueItem({
            id: orderId,
          })
        }
      },
    })
  const [orderSimCard, { error: orderSimCardError, ...orderSimCardResult }] =
    useAppMutation(OrderSimCardDocument, {
      onCompleted: (d) => {
        const orderId = d?.orderSimCard?.orderId
        setOrderId(orderId)
        pushOrderQueueItem({
          id: orderId,
        })
      },
    })
  const errors = { addSimCardError, orderSimCardError }
  const [zipCode, setZipCode] = useState<string>("")
  const [zipCity, setZipCity] = useState<string>("")
  const [lookupZipCode, { data: zipData, called }] = useAppLazyQuery(
    SimZipCodeDocument,
    {}
  )
  useEffect(() => {
    if (zipData?.zipCodes?.length) {
      setZipCode(zipData.zipCodes[0].zipCode)
      setZipCity(zipData.zipCodes[0].city)
    }
  }, [zipData])
  if (!called && data?.address?.zipCodeId) {
    const id = Number(data.address.zipCodeId)
    lookupZipCode({ variables: { zipCode: { id } } })
  }
  const done =
    (addSimcardResult.called &&
      !addSimcardResult.loading &&
      !addSimCardError) ||
    (orderSimCardResult.called &&
      !orderSimCardResult.loading &&
      !orderSimCardError)
  const delayActivation =
    card?.state === ApiProductState.Active &&
    cardType === SimCardType2.Subscription &&
    !data?.iccid
  const vippsRecurringCallback =
    getCurrentAppScheme() +
    `${routes.paymentCallbackVippsRecurring.path.slice(1)}?sessionID=${
      faroClient?.metas?.value?.session?.id || ""
    }`

  const onConfirm = (paymentMethod: PaymentMethod) => {

    if((currentProduct.paymentMethods?.indexOf(paymentMethod) ?? -1) < 0) {
        console.error(
          "the current product does not support the payment method selected ",
          { currentProduct, paymentMethod }
        )
        return
    }
    if (card?.state === ApiProductState.Active) {
      const variables: OrderSimCardMutationVariables = {
        input: {
          id: card.id,
          address: data?.address || address,
          iccid: sendSimcard ? undefined : data?.iccid,
          delayActivation: delayActivation,
          simWithoutPin: simWithoutPin && sendSimcard,
          embeddedSim: embeddedSim,
          embeddedSimEmail: embeddedSimEmail,
          cardType: card.type,
        },
      }
      orderSimCard({
        variables,
      })
    } else {
      const variables: AddSimCardMutationVariables = {
        input: {
          subscriptionId,
          cardType: cardType,
          address: data?.address || address,
          iccid: sendSimcard ? undefined : data?.iccid,
          embeddedSim: embeddedSim,
          embeddedSimEmail: embeddedSimEmail,
          paymentMethod: currentProduct.paymentMethods?.length
            ? currentProduct.paymentMethods[0]
            : undefined,
          vippsRecurring: isPaymentMethodVippsRecurring
            ? {
                productId: currentProduct.productId,
                callbackUrl: vippsRecurringCallback,
                //callbackUrl: "https://ditt.dev-selfservice-lor.phonero.telia.io/paymentCallback/VippsRecurring/:paymentID?sessionID=1234",
                agreementUrl: `${getCurrentAppScheme()}tjenester`,
              }
            : undefined,
        },
      }
      addSimcard({
        variables,
      })
    }
  }
  // Iccid is 19 or 20 digits long, but only the 12 last digits are displayed on the SIM letter.
  // Use Iccid full length for ordering SIM and use displayIccid for output to user.
  const displayIccid =
    data?.iccid?.substring(data?.iccid?.length - 12) || undefined
  return (
    <>
      <PuxContent>
        <div className={css["summary-header"]}>
          <PuxTypography variant={"h3"}>
            {userName} -{" "}
            {msisdn ? formatTelephoneNumber(msisdn).formatted : null}
          </PuxTypography>
        </div>
        <InlineErrors errors={errors} />
        {(addSimcardResult?.called || orderSimCardResult?.called) && (
          <div>
            <OrderStatus
              statusText={t("SingleOrderStatusChecker.ChangesDoneInfoText")}
              hasError={!!addSimCardError || !!orderSimCardError}
              orderCountTotal={1}
              orderCountCancelled={0}
              orderCountCompleted={done ? 1 : 0}
              statusMessages={[
                delayActivation
                  ? t(
                      "AddSubscriptionWizard.Step5.SendingSimCardDelayedActivation"
                    )
                  : "",
              ]}
              isSafeToClose={done}
              safeToCloseMessages={[]}
            />
          </div>
        )}
        <div>
          <div className={css["summary-container"]}>
            <div>
              <span style={{ fontWeight: 700 }}>Oppsummering</span>
            </div>

            {!embeddedSim && (
              <div>
                <span>{t("SimCard.Type")}</span>
                <span>{t("SimCard.Type", { context: cardType })}</span>
              </div>
            )}

            {!embeddedSim && (
              <>
                <div>
                  <span>{t("SimCard.SimClassification")}</span>
                  <div>
                    <span>
                      {t("AddSubscriptionWizard.Step4.PhysicalSimCard")}
                    </span>
                  </div>
                </div>
                {sendSimcard && (
                  <div>
                    <span>{sendSimcard && t("Address.Address1")}</span>
                    <div>
                      <span>{sendSimcard && `${data?.address?.name}`}</span>
                      {sendSimcard && data?.address?.address1 && (
                        <>
                          <span></span>
                          <span>{`${data?.address.address1}`}</span>
                        </>
                      )}
                      {sendSimcard && data?.address?.address2 && (
                        <>
                          <span></span>
                          <span>{`${data?.address.address2}`}</span>
                        </>
                      )}
                      {sendSimcard && zipCode && zipCity && (
                        <>
                          <span></span>
                          <span>{`${zipCode} ${zipCity}`}</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
            {embeddedSim && responsiveMode === ResponsiveMode.Desktop && (
              <>
                <div>
                  <span>{t("SimCard.Type")}</span>
                  <span>{t("SimCard.Type", { context: cardType })}</span>
                </div>
                <div>
                  <span>{t("SimCard.SimClassification")}</span>
                  <div>
                    <span>
                      {t("AddSubscriptionWizard.Step4.EmbeddedSimCard")}
                    </span>
                  </div>
                </div>

                <div>
                  <span>
                    {t(
                      "AddSubscriptionWizard.Step5.EmbeddedSimDeliveryAddress"
                    )}
                  </span>

                  <div>
                    <span>{`${embeddedSimEmail}`}</span>
                  </div>
                </div>
              </>
            )}

            {embeddedSim && responsiveMode === ResponsiveMode.Mobile && (
              <>
                <div>
                  <div className={css["mobile-embedd"]}>
                    <span>{t("SimCard.Type")}</span>
                    <span>{t("SimCard.Type", { context: cardType })}</span>
                  </div>
                </div>
                <div>
                  <div className={css["mobile-embedd"]}>
                    <span>{t("SimCard.SimClassification")}</span>

                    <span>
                      {t("AddSubscriptionWizard.Step4.EmbeddedSimCard")}
                    </span>
                  </div>
                </div>

                <div>
                  <div className={css["mobile-embedd"]}>
                    <span>
                      {t(
                        "AddSubscriptionWizard.Step5.EmbeddedSimDeliveryAddress"
                      )}
                    </span>
                    <span>{`${embeddedSimEmail}`}</span>
                  </div>
                </div>
              </>
            )}

            {itemWithPrice?.state === ApiProductState.Available && (
              <div>
                <span>
                  {t("AddSubscriptionWizard.Step5.EstablishmentPrice")}
                </span>
                <span
                  className={
                    card?.establishmentPrice === 0 ? css["green-text"] : ""
                  }
                >
                  {i18n.format(card?.establishmentPrice ?? 0, "price")}
                  {/* {",- "} */}
                </span>
              </div>
            )}
            {itemWithPrice?.state === ApiProductState.Available && (
              <div>
                <span>
                  {t("AddSubscriptionWizard.Step5.MonthlyPriceAlternative")}
                </span>
                <span className={itemWithPrice?.price === 0 ? css["green-text"] : ""}>
                  {i18n.format(itemWithPrice?.price)}
                  {",- "}
                </span>
              </div>
            )}
            {card?.state === ApiProductState.Active &&
              responsiveMode === ResponsiveMode.Desktop && (
                <div>
                  <span>
                    {t("AddSubscriptionWizard.Step5.EstablishmentPrice")}
                  </span>
                  {card.__typename === "SimCard" && (
                    <span
                      className={classNames(
                        (embeddedSim || card?.replacementPrice === 0) &&
                          css["green-text"]
                      )}
                    >
                      {embeddedSim
                        ? i18n.format(
                            card?.embeddedSimReplacementPrice ?? 0,
                            "price"
                          )
                        : i18n.format(card?.replacementPrice ?? 0, "price")}
                    </span>
                  )}
                </div>
              )}
            {card?.state === ApiProductState.Active &&
              responsiveMode === ResponsiveMode.Mobile && (
                <div className={css["mobile-embedd"]}>
                  <span>
                    {t("AddSubscriptionWizard.Step5.EstablishmentPrice")}
                  </span>
                  {card.__typename === "SimCard" && (
                    <span
                      className={classNames(
                        (embeddedSim || card?.replacementPrice === 0) &&
                          css["green-text"]
                      )}
                    >
                      {embeddedSim
                        ? i18n.format(
                            card?.embeddedSimReplacementPrice ?? 0,
                            "price"
                          )
                        : i18n.format(card?.replacementPrice ?? 0, "price")}
                    </span>
                  )}
                </div>
              )}
            {children}
          </div>
        </div>
        <div style={{ paddingBottom: 30, paddingTop: 10 }}>
          <PriceMvaInfo />
        </div>
      </PuxContent>
      <PuxFooter>
        <StepButtons
          {...(done
            ? { onDone: onClose }
            : {
                onPrev,               
                ...(isPaymentMethodMobile ? {onConfirm : () => onConfirm(PaymentMethod.Mobile)} : {}),
                ...(isPaymentMethodVippsRecurring ? {onPayWithVipps: () => onConfirm(PaymentMethod.Vippsrecurring)} : {}),
                payWithVippsText: "Kjøp privat med vipps"
              })}
        />
      </PuxFooter>
    </>
  )
}
